import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Gauge } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchCarry, fetchBias } from "@/lib/api";
import { computeCrowding } from "@/lib/signals";

const CCYS = ["USD", "EUR", "GBP", "XAU"];
const SYM = { USD: "DXY", EUR: "EUR", GBP: "GBP", XAU: "GOLD" };

const Chip = ({ label, value, tone }) => {
  const cls =
    tone === "risk" ? "border-red-500/50 text-neg bg-red-950/20"
    : tone === "on" ? "border-emerald-500/50 text-pos bg-emerald-950/20"
    : tone === "warn" ? "border-amber-500/50 text-amber-term bg-amber-950/20"
    : "border-zinc-700 text-zinc-300";
  return (
    <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-[11px] uppercase tracking-widest ${cls}`}>
      <span className="text-zinc-600">{label}</span>
      <span className="font-semibold">{value}</span>
    </span>
  );
};

// One-glance market-regime tag for a selectable asset (USD/EUR/GBP/XAU).
export default function RegimeBar({ markets = [], vix, threshold }) {
  const [ccy, setCcy] = useState("USD");
  const carryQ = useQuery({ queryKey: ["carry"], queryFn: fetchCarry, staleTime: 1000 * 60 * 60 * 3, refetchOnWindowFocus: false });
  const biasQ = useQuery({ queryKey: ["bias", ccy], queryFn: () => fetchBias(ccy), staleTime: 1000 * 60 * 5 });

  // Risk regime (market-wide)
  let risk = "NEUTRAL", riskTone = "warn";
  if (vix != null) {
    const off = (threshold != null && vix >= threshold) || vix >= 20;
    if (off) { risk = "OFF"; riskTone = "risk"; }
    else if (vix < 15) { risk = "ON"; riskTone = "on"; }
  }

  // Positioning read for the selected asset
  let posDir = null, stretched = false;
  if (ccy === "USD") {
    const c = computeCrowding(markets);
    if (c) { posDir = c.dominant; stretched = c.strong; }
  } else {
    const m = markets.find((x) => x.symbol === SYM[ccy]);
    const idx = m?.cot_index_1y;
    if (idx != null) {
      posDir = idx >= 50 ? "long" : "short";
      stretched = !!m.extreme || idx >= 82 || idx <= 18;
    }
  }

  // Asset bias regime
  const dir = biasQ.data?.bias;
  let assetVal = "MIXED", assetTone = "neutral";
  if (dir === "bullish") { assetVal = "BID"; assetTone = "on"; }
  else if (dir === "bearish") { assetVal = "SOFT"; assetTone = "risk"; }
  if (stretched && posDir) assetVal = `${assetVal === "MIXED" ? "" : assetVal + " · "}STRETCHED ${posDir === "long" ? "LONG" : "SHORT"}`;

  // Carry regime
  const topCcy = carryQ.data?.rows?.[0]?.ccy;
  let carry = "NEUTRAL", carryTone = "neutral";
  if (risk === "OFF") { carry = "UNWIND"; carryTone = "risk"; }
  else if (risk === "ON") { carry = topCcy ? `ON · ${topCcy}` : "ON"; carryTone = "on"; }

  // Headline
  let headline = "NEUTRAL", hTone = "warn";
  if (risk === "OFF") { headline = "RISK-OFF"; hTone = "risk"; }
  else if (stretched && posDir) { headline = `${ccy} STRETCHED ${posDir === "long" ? "LONG" : "SHORT"}`; hTone = "warn"; }
  else if (risk === "ON") { headline = "RISK-ON"; hTone = "on"; }
  const hCls = hTone === "risk" ? "text-neg" : hTone === "on" ? "text-pos" : "text-amber-term";

  return (
    <div className="border border-zinc-800 bg-[#09090B] px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="regime-bar">
      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-500">
        <Gauge size={13} strokeWidth={1.5} /> Market Regime
      </span>
      <Select value={ccy} onValueChange={setCcy}>
        <SelectTrigger data-testid="regime-ccy-select" className="h-6 w-[78px] text-[11px] bg-[#09090B] border-zinc-700 focus:ring-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#09090B] border-zinc-700">
          {CCYS.map((c) => (
            <SelectItem key={c} value={c} data-testid={`regime-ccy-${c}`} className="text-[11px] focus:bg-zinc-800 focus:text-amber-term">{c}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className={`text-sm font-semibold ${hCls}`} data-testid="regime-headline">{headline}</span>
      <span className="h-3 w-px bg-zinc-800 hidden sm:inline-block" />
      <div className="flex items-center gap-2 flex-wrap">
        <Chip label="risk" value={risk} tone={riskTone} />
        <Chip label={ccy.toLowerCase()} value={assetVal} tone={assetTone} />
        <Chip label="carry" value={carry} tone={carryTone} />
        {vix != null && (
          <span className="text-[11px] uppercase tracking-widest text-zinc-600 tabular-nums">VIX {Number(vix).toFixed(1)}</span>
        )}
      </div>
    </div>
  );
}
